import { usePeopleTableColumns } from 'components/People/Table';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTableSortableHeaderCellEffectHandlers } from 'shell/components';
import { downloadCSV, getPrettySections, getUserPreferredFullName } from 'utils/helper';

const handleFilters = (items, filters, setItems) => {
    const STAFF_ROLES = ['instructor', 'ta'];
    const search = filters.search.toLowerCase();

    const res = items.filter((item) => {
        const matchesSearch = getUserPreferredFullName(item).toLowerCase().includes(search);

        const matchesRole =
            filters.roles.length === 0 ||
            (filters.roles.includes('staff') && STAFF_ROLES.includes(item.role)) ||
            (filters.roles.includes('student') && item.role === 'student');

        return matchesSearch && matchesRole;
    });

    setItems(res);
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, accountUsers, setAccountUsers, studentAccountUsers, changeAccountUserRole } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation();
    const columns = usePeopleTableColumns();

    const [filters, setFilters] = useState({
        open: false,
        roles: [],
        search: '',
    });
    const [checked, setChecked] = useState([]);
    const [syncRosterDialog, setSyncRosterDialog] = useState({ open: false });

    const {
        sortedFilteredData: sortedFilteredAccountUsers,
        sortState,
        handleSetSortState,
    } = useTableSortableHeaderCellEffectHandlers(accountUsers, columns, filters, setAccountUsers, handleFilters);

    const resetFilters = useCallback(() => {
        setFilters((current) => ({
            ...current,
            roles: [],
        }));
        handleSetSortState({ order: 'asc', orderBy: null });
    }, [handleSetSortState]);

    const getFilterCount = (filters, sortState) => {
        let count = 0;
        if (sortState.orderBy) count++;
        if (filters.roles.length) count++;
        return count;
    };

    const downloadCourseRoster = useCallback(() => {
        const isLti = process.env.REACT_APP_AUTH === 'LTI';
        const data = (studentAccountUsers || []).map((student) => {
            return [
                student.display_name || student.first_name || '',
                student.last_name || '',
                isLti ? student.username : student.email,
                getPrettySections(student.sections),
            ];
        });
        return downloadCSV(`${course.name}_roster_template.csv`.toLowerCase().replaceAll(' ', '_'), [
            [
                t('table.first-name', 'First Name'),
                t('table.last-name', 'Last Name'),
                t('table.email', 'Email'),
                t('table.section-s', 'Section(s)'),
            ],
            ...data,
        ]);
    }, [course?.name, studentAccountUsers, t]);

    const getOptions = useCallback(
        (accountUser) => {
            return [
                { key: 'copy-email', value: t('people.copy-email', 'Copy Email') },
                ...(accountUser.owner_account_id !== auth.owner_account_id && accountUser.role === 'instructor'
                    ? [{ key: 'update-role-to-ta', value: t('people.change-to-ta', 'Change to TA') }]
                    : []),
                ...(accountUser.owner_account_id !== auth.owner_account_id && accountUser.role === 'ta'
                    ? [
                          {
                              key: 'update-role-to-instructor',
                              value: t('people.change-to-instructor', 'Change to Instructor'),
                          },
                      ]
                    : []),
            ];
        },
        [auth.owner_account_id, t]
    );

    const handleSelect = useCallback(
        async ({ key }, accountUser) => {
            if (key === 'copy-email') {
                await navigator.clipboard.writeText(accountUser.email);
                message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
            }
            if (key === 'update-role-to-ta' || key === 'update-role-to-instructor') {
                setDialog({
                    open: true,
                    message: t('dialog.change-role.title', 'Confirm Role Change?'),
                    description: t(
                        'dialog.change-role.description',
                        'Visibility is role-based. Instructors and TAs only see what their permissions allow.'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('controls.confirm', 'Confirm'),
                    cancelButtonText: t('controls.cancel', 'Cancel'),
                    onAction: () => {
                        closeDialog();
                        changeAccountUserRole(accountUser, key === 'update-role-to-ta' ? 'ta' : 'instructor');
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }
        },
        [changeAccountUserRole, closeDialog, message, setDialog, t]
    );

    const value = useMemo(
        () => ({
            accountUsers: sortedFilteredAccountUsers,
            sortState,
            handleSetSortState,
            filters,
            setFilters,
            resetFilters,
            getFilterCount,
            checked,
            setChecked,
            downloadCourseRoster,
            columns,
            syncRosterDialog,
            setSyncRosterDialog,
            getOptions,
            handleSelect,
        }),
        [
            checked,
            downloadCourseRoster,
            filters,
            getOptions,
            handleSelect,
            handleSetSortState,
            resetFilters,
            sortState,
            sortedFilteredAccountUsers,
            columns,
            syncRosterDialog,
        ]
    );

    return <PageContext.Provider value={value} {...props} />;
};

export { PageContext };
export default PageProvider;
